// =============================================================================
// ActiveBanners.jsx
// =============================================================================
// Site-wide announcement strip shown to every logged-in user. Pulls active rows
// from the `banners` table and filters them to the current viewer's audience,
// state and expiry.
//
// Mounted ONCE in AppShell (live-lists overhaul Batch B) — it used to be pasted
// onto three dashboards and so was absent from every other screen. It reads the
// viewer from `useSession()` (no prop) and keeps itself fresh via Supabase
// Realtime on the `banners` table plus a slow safety poll.
//
// The strip is STATIC when the message fits and only falls back to a seamless
// (gap-free) scroll when the text genuinely overflows the available width — the
// old build scrolled everything, so short messages sat off-screen for most of
// the loop. Overflow is measured live and re-checked on resize.
//
// Pulled out of AdminPages.jsx as part of the Admin file split (May 2026).
// =============================================================================
import React from "react";
import { useSession } from "../../app/SessionContext.jsx";
import { listActiveBanners } from "../../data/banners";
import { bannerMatchesUser } from "../../services/bannerAudience";
import { getSupabaseClient } from "../../utils/supabase";

const STYLE_MAP = {
  info: { bg: "#E3F2FD", border: "#90CAF9", text: "#1565C0" },
  warning: { bg: "#FFF8E1", border: "#FFD54F", text: "#5D4037" },
  urgent: { bg: "#FFEBEE", border: "#EF9A9A", text: "#C62828" },
  success: { bg: "#E8F5E9", border: "#A5D6A7", text: "#2E7D32" },
};

// How long the banner holds still before each scroll pass (seconds).
const BANNER_HOLD_S = 60;

// A single banner row (John's spec, 3 Sep 2026): EVERY banner — regardless of length —
// sits CENTRED and readable, holds for BANNER_HOLD_S, then does ONE smooth right-to-left
// pass and settles back centred, holding again — repeating. A short message that fits does
// a full marquee lap (off the left, in from the right, back to centre); a long one reveals
// its whole length as it passes. Implemented with two identical copies separated by a
// one-strip gap: the pass ends with the SECOND copy centred, which looks identical to the
// FIRST copy centred, so the loop restart is seamless (no jump). All distances are measured
// against the live strip and re-checked on resize; a constant px/s speed keeps reading
// pace steady across lengths.
function BannerStrip({ message, colours }) {
  const boxRef = React.useRef(null);
  const spanRef = React.useRef(null);
  // { center, step, gap, scrollS } once measured; null pre-measure (render static-centred).
  const [anim, setAnim] = React.useState(null);
  // A CSS-ident-safe unique keyframe name per banner (useId can contain ":").
  const animName = `bnr${React.useId().replace(/[^a-zA-Z0-9]/g, "")}`;

  React.useLayoutEffect(
    function () {
      function measure() {
        const box = boxRef.current;
        const span = spanRef.current;
        if (!box || !span) return;
        const W = box.clientWidth;
        const M = span.scrollWidth;
        if (!W || !M) return;
        const gap = W; // a clear one-strip gap between the repeating copies
        const center = (W - M) / 2; // offset that centres a copy in the strip
        const step = M + gap; // distance that brings the 2nd copy to the centre
        const scrollS = Math.max(6, Math.round(step / 80)); // ~80px/s, constant pace
        setAnim({ center, step, gap, scrollS });
      }
      measure();
      let ro = null;
      if (typeof ResizeObserver !== "undefined" && boxRef.current) {
        ro = new ResizeObserver(measure);
        ro.observe(boxRef.current);
      }
      return function () {
        if (ro) ro.disconnect();
      };
    },
    [message]
  );

  const spanStyle = { color: colours.text, paddingInline: 24 };

  // Pre-measure (and no-JS) fallback: centred + static.
  if (!anim) {
    return (
      <div ref={boxRef} className="overflow-hidden w-full">
        <div className="flex justify-center whitespace-nowrap">
          <span ref={spanRef} style={spanStyle} className="text-sm font-semibold">
            {message}
          </span>
        </div>
      </div>
    );
  }

  // Cycle = hold centred (BANNER_HOLD_S) + one pass. The keyframe holds at translateX(center)
  // — copy 1 centred — then scrolls to translateX(center - step) — copy 2 centred. The loop
  // restart (100% → 0%) swaps an identical centred copy for another, so there is no jump.
  const totalS = BANNER_HOLD_S + anim.scrollS;
  const holdPct = ((BANNER_HOLD_S / totalS) * 100).toFixed(2);

  return (
    <div ref={boxRef} className="overflow-hidden w-full">
      <style>
        {`@keyframes ${animName} { 0%, ${holdPct}% { transform: translateX(${anim.center}px); } 100% { transform: translateX(${anim.center - anim.step}px); } }`}
      </style>
      <div
        className="inline-flex whitespace-nowrap"
        style={{ animation: `${animName} ${totalS}s linear infinite` }}
      >
        <span ref={spanRef} style={spanStyle} className="text-sm font-semibold">
          {message}
        </span>
        <span
          aria-hidden="true"
          style={{ ...spanStyle, marginInlineStart: anim.gap }}
          className="text-sm font-semibold"
        >
          {message}
        </span>
      </div>
    </div>
  );
}

export function ActiveBanners() {
  const { user } = useSession();
  const [banners, setBanners] = React.useState([]);

  // The viewer is held in a ref so the single Realtime subscription's handler always
  // filters against the latest user without needing to tear down + re-subscribe.
  const userRef = React.useRef(user);
  userRef.current = user;

  React.useEffect(
    function () {
      let cancelled = false;

      function refresh() {
        const u = userRef.current;
        if (!u) {
          if (!cancelled) setBanners([]);
          return;
        }
        const today = new Date().toISOString().slice(0, 10);
        listActiveBanners().then(function (rows) {
          if (cancelled || !rows) return;
          const filtered = rows.filter(function (b) {
            const audOk = bannerMatchesUser(b.audience, u);
            const stateOk = !b.states || b.states.length === 0 || b.states.includes(u.state);
            const notExpired = !b.expires_at || b.expires_at >= today;
            return audOk && stateOk && notExpired;
          });
          setBanners(filtered);
        });
      }

      refresh();

      // B3: Realtime on the tiny `banners` table — a newly published / edited / expired /
      // deactivated banner appears (or disappears) instantly for everyone, no reload. The
      // whole active list is re-filtered on any change (the table is tiny, so no
      // delta-apply is needed). Requires banners in the supabase_realtime publication
      // (db-191); if that isn't enabled yet the subscription simply gets no events and the
      // safety poll below still keeps the strip current — the code fails open.
      const client = getSupabaseClient();
      let channel = null;
      if (client) {
        channel = client
          .channel("banners-watch")
          .on("postgres_changes", { event: "*", schema: "public", table: "banners" }, function () {
            refresh();
          })
          .subscribe();
      }

      // Safety net: a slow poll catches expiry (time passing fires no DB event) and any
      // Realtime event missed across a dropped socket. Cheap — one small read every 3 min.
      const pollId = setInterval(refresh, 180000);

      return function () {
        cancelled = true;
        clearInterval(pollId);
        if (client && channel) client.removeChannel(channel);
      };
    },
    // Re-subscribe only when the viewer's identity/audience inputs change (rare); the
    // handler reads userRef so filtering stays current even between these.
    [user?.id, user?.role, user?.state]
  );

  if (banners.length === 0) return null;

  return (
    <div className="mb-4">
      {banners.map(function (b) {
        const st = STYLE_MAP[b.style] || STYLE_MAP.info;
        return (
          <div
            key={b.id}
            style={{
              background: st.bg,
              borderTop: `3px solid ${st.border}`,
              borderBottom: `3px solid ${st.border}`,
            }}
            className="py-[10px] mb-2 w-screen relative left-1/2 -translate-x-1/2"
          >
            <BannerStrip message={b.message} colours={st} />
          </div>
        );
      })}
    </div>
  );
}
